import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { locationsRes, safe } from "../../lib/locations";
import ServiceDueView from "./ServiceDueView";
import { toast } from "sonner";
import {
  Users, UserPlus, ArrowsClockwise, Path, CheckCircle,
  MagnifyingGlass, FunnelSimple, Wallet, Warning, Receipt, Clock,
  ArrowRight, X, CaretUp, CaretDown, Calendar as CalIcon,
} from "@phosphor-icons/react";

const DAYS = [
  { code: "mon", label: "Mon" }, { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" }, { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" }, { code: "sat", label: "Sat" },
  { code: "sun", label: "Sun" },
];

export default function Assignments() {
  const [tab, setTab] = useState("matrix");   // matrix | calendar | route
  const [filters, setFilters] = useState({
    district_id: "", lsgd_id: "", ward_id: "", health_zone_id: "",
    field_staff_id: "", office_staff_id: "", vehicle_id: "", day: "",
    unassigned: false,
  });
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [officeUsers, setOfficeUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  // Iter 167 — location + search state
  const [locations, setLocations] = useState({ districts: [], lsgds: [], wards: [], zones: [] });
  const [dropLocations, setDropLocations] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);

  // Reference lists load once, independently of the (slow) matrix call.
  useEffect(() => {
    const arrayify = (d) => Array.isArray(d) ? d : (d?.items || []);
    safe(api.get("/staff")).then(r => setStaff(arrayify(r.data)));
    safe(api.get("/office-staff")).then(r => setOfficeUsers(arrayify(r.data)));
    safe(api.get("/vehicles")).then(r => setVehicles(arrayify(r.data)));
    safe(api.get("/drop-locations")).then(r => setDropLocations(arrayify(r.data)));
    Promise.all([locationsRes(), safe(api.get("/zones"))]).then(([loc, zones]) => {
      const locs = arrayify(loc.data);
      setLocations({
        districts: locs.filter(l => l.type === "district"),
        lsgds:     locs.filter(l => l.type === "lsgd"),
        wards:     locs.filter(l => l.type === "ward"),
        zones:     arrayify(zones.data),
      });
    });
  }, []);

  const loadAll = async (signal) => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (q.trim()) params.q = q.trim();
      // Iter 182 · only the matrix call needs cancel + light mode —
      // the other reference lists rarely change and don't block the pool.
      const matrixP = api.get(
        `/operations/assignments/matrix?${new URLSearchParams(
          Object.entries(params).filter(([, v]) => v)
        )}`,
        { signal }
      );
      const [c, s] = await Promise.all([matrixP, api.get("/operations/assignments/summary")]);
      setCustomers(c.data.customers || []);
      setSummary(s.data);
    } catch (e) {
      // Silently swallow AbortError from typeahead cancel.
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError"
           || e?.name === "AbortError") { setLoading(false); return; }
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  // Iter 182 · debounce the typeahead (300 ms) + cancel in-flight
  // matrix requests so a fast typer stops firing 1 req/keystroke and
  // draining the Mongo pool in production.
  const abortRef = React.useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      loadAll(abortRef.current.signal);
    }, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, q]);

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    setSelected(selected.size === customers.length ? new Set() : new Set(customers.map(c => c.id)));
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="assignments-page">
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
            Operations · Fixed responsibility
          </div>
          <h1 className="font-heading text-3xl mt-1 flex items-center gap-2">
            <Users size={22} weight="duotone" color="#2D5A27"/>
            Assign Field & Office Staff
          </h1>
          <div className="text-[11px] text-[#5C5A55] mt-1">
            Fixed customer → field staff · office staff · vehicle · service days.
            Temporary overrides never touch the permanent route.
          </div>
        </div>

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Tile label="Field staff active" value={summary.per_field_staff.length}/>
            <Tile label="Office coords" value={summary.per_office_staff.length}/>
            <Tile label="Vehicles in use" value={summary.per_vehicle.length}/>
            <Tile label="Unassigned" value={summary.unassigned} tone="danger"/>
            <Tile label="Route days"
                    value={summary.per_day.filter(d => d.count > 0).length + " / 7"}/>
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex rounded-md border border-[#E5E2D9] overflow-hidden mb-4 flex-wrap">
          {["matrix", "calendar", "map", "route", "roster", "duty", "my-customers", "service-due"].map(t => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`assign-tab-${t}`}
                    className={`px-3 h-9 text-xs font-medium capitalize ${
                      tab === t ? "bg-[#2D5A27] text-white" : "bg-white text-[#5C5A55] hover:bg-[#F7F6F2]"
                    }`}>
              {t === "matrix" ? "Assignment list"
                : t === "calendar" ? "Calendar"
                : t === "map" ? "Map view"
                : t === "route" ? "Daily route"
                : t === "roster" ? "Customer roster"
                : t === "duty" ? "Duty roster"
                : t === "service-due" ? "Service due / delayed"
                : "My Responsible Customers"}
            </button>
          ))}
        </div>

        {tab === "matrix" && (
          <MatrixView filters={filters} setFilters={setFilters}
                       staff={staff} officeUsers={officeUsers} vehicles={vehicles}
                       locations={locations} q={q} setQ={setQ}
                       customers={customers} selected={selected}
                       toggleSelect={toggleSelect} toggleAll={toggleAll}
                       loading={loading}
                       onAssign={() => setShowAssignModal(true)}
                       onHistory={setHistoryFor}/>
        )}
        {tab === "calendar" && summary && <CalendarView summary={summary}/>}
        {tab === "map" && (
          <MapView customers={customers} staff={staff} officeUsers={officeUsers}
                    filters={filters} setFilters={setFilters}/>
        )}
        {tab === "route" && (
          <RouteBuilder staff={staff} vehicles={vehicles} dropLocations={dropLocations}/>
        )}
        {tab === "roster" && (
          <RosterView customers={customers} staff={staff} officeUsers={officeUsers}
                       filters={filters} setFilters={setFilters} onRefresh={loadAll}/>
        )}
        {tab === "duty" && <DutyRosterView/>}
        {tab === "my-customers" && (
          <MyResponsibleCustomers officeUsers={officeUsers} locations={locations}/>
        )}
        {tab === "service-due" && <ServiceDueView staff={staff} locations={locations}/>}

        {showAssignModal && (
          <AssignModal selectedIds={[...selected]}
                        staff={staff} officeUsers={officeUsers} vehicles={vehicles}
                        onClose={() => setShowAssignModal(false)}
                        onDone={() => { setShowAssignModal(false); setSelected(new Set()); loadAll(); }}/>
        )}
        {historyFor && (
          <HistoryModal customerId={historyFor} onClose={() => setHistoryFor(null)}/>
        )}
      </div>
    </AppShell>
  );
}

function Tile({ label, value, tone }) {
  const cls = tone === "danger"
    ? "bg-[#FBEBE5] border-[#EFC7B6]"
    : "bg-white border-[#E5E2D9]";
  return (
    <div className={`border rounded-md p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">{label}</div>
      <div className="font-heading text-2xl mt-1">{value}</div>
    </div>
  );
}

function MatrixView({ filters, setFilters, staff, officeUsers, vehicles, locations, q, setQ, customers, selected, toggleSelect, toggleAll, loading, onAssign, onHistory }) {
  const upd = (k, v) => setFilters({ ...filters, [k]: v });
  // Iter 167 — LSGDs and wards filter by parent when parent is chosen.
  const lsgdOpts = filters.district_id
    ? locations.lsgds.filter(l => l.parent_id === filters.district_id)
    : locations.lsgds;
  const wardOpts = filters.lsgd_id
    ? locations.wards.filter(w => w.parent_id === filters.lsgd_id)
    : locations.wards;
  return (
    <>
      <div className="bg-white border border-[#E5E2D9] rounded-md p-3 mb-4">
        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
            Customer search
          </label>
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Type name, phone or customer code…"
                 data-testid="assign-cust-search"
                 className="mt-1 w-full h-9 px-3 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
          <FilterSelect label="District" value={filters.district_id}
                         onChange={v => setFilters({ ...filters, district_id: v, lsgd_id: "", ward_id: "" })}
                         options={locations.districts.map(l => ({ id: l.id, label: l.name }))}
                         testid="f-district"/>
          <FilterSelect label="LSGD" value={filters.lsgd_id}
                         onChange={v => setFilters({ ...filters, lsgd_id: v, ward_id: "" })}
                         options={lsgdOpts.map(l => ({ id: l.id, label: l.name }))}
                         testid="f-lsgd"/>
          <FilterSelect label="Ward" value={filters.ward_id}
                         onChange={v => upd("ward_id", v)}
                         options={wardOpts.map(l => ({ id: l.id, label: l.name }))}
                         testid="f-ward"/>
          <FilterSelect label="Health zone" value={filters.health_zone_id}
                         onChange={v => upd("health_zone_id", v)}
                         options={locations.zones.map(z => ({ id: z.id, label: z.name }))}
                         testid="f-hz"/>
          <FilterSelect label="Field staff" value={filters.field_staff_id}
                         onChange={v => upd("field_staff_id", v)}
                         options={staff.map(s => ({ id: s.id, label: s.name }))}
                         testid="f-fs"/>
          <FilterSelect label="Office staff" value={filters.office_staff_id}
                         onChange={v => upd("office_staff_id", v)}
                         options={officeUsers.map(s => ({ id: s.id, label: s.name || s.email }))}
                         testid="f-os"/>
          <FilterSelect label="Vehicle" value={filters.vehicle_id}
                         onChange={v => upd("vehicle_id", v)}
                         options={vehicles.map(v => ({ id: v.id, label: v.vehicle_no || v.registration_no || v.number || "—" }))}
                         testid="f-veh"/>
          <FilterSelect label="Service day" value={filters.day}
                         onChange={v => upd("day", v)}
                         options={DAYS.map(d => ({ id: d.code, label: d.label }))}
                         testid="f-day"/>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8780]">
          {customers.length} customer{customers.length === 1 ? "" : "s"} · {selected.size} selected
          {loading && <span className="ml-2 italic">loading…</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleAll} data-testid="assign-select-all"
                  className="h-8 px-3 rounded border border-[#E5E2D9] text-xs">
            {selected.size === customers.length ? "Clear all" : "Select all"}
          </button>
          <button onClick={onAssign} disabled={selected.size === 0}
                  data-testid="assign-open-modal"
                  className="h-8 px-3 rounded bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-xs font-semibold inline-flex items-center gap-1">
            <UserPlus size={12} weight="fill"/>
            Assign {selected.size > 0 && `(${selected.size})`}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">
            <tr>
              <th className="px-2 py-2 w-8"></th>
              <th className="text-left px-2 py-2">Customer</th>
              <th className="text-left px-2 py-2">Field staff</th>
              <th className="text-left px-2 py-2">Office staff</th>
              <th className="text-left px-2 py-2">Vehicle</th>
              <th className="text-left px-2 py-2">Days</th>
              <th className="text-left px-2 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0ECE3]">
            {customers.map(c => (
              <tr key={c.id} data-testid={`assign-row-${c.id}`}
                  className={selected.has(c.id) ? "bg-[#F0F7EE]" : ""}>
                <td className="px-2 py-1.5 text-center">
                  <input type="checkbox" checked={selected.has(c.id)}
                         onChange={() => toggleSelect(c.id)}
                         data-testid={`assign-check-${c.id}`}
                         className="accent-[#2D5A27]"/>
                </td>
                <td className="px-2 py-1.5">
                  <div className="font-medium truncate max-w-[220px]">{c.name}</div>
                  <div className="text-[10px] text-[#8A8780]">{c.phone} {c.customer_code && <>· {c.customer_code}</>}</div>
                </td>
                <td className="px-2 py-1.5">{c.responsible_field_staff_name || <span className="text-[#8A8780] italic">—</span>}</td>
                <td className="px-2 py-1.5">{c.responsible_office_staff_name || <span className="text-[#8A8780] italic">—</span>}</td>
                <td className="px-2 py-1.5">{c.assigned_vehicle_no || <span className="text-[#8A8780] italic">—</span>}</td>
                <td className="px-2 py-1.5 text-[11px]">
                  {(c.service_days || []).length > 0
                    ? (c.service_days || []).map(d => d.toUpperCase()).join(" · ")
                    : <span className="text-[#8A8780] italic">—</span>}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button onClick={() => onHistory(c.id)}
                          data-testid={`assign-hist-${c.id}`}
                          className="text-[11px] text-[#0B2B66] underline">
                    History
                  </button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && !loading && (
              <tr><td colSpan="7" className="p-8 text-center text-sm text-[#8A8780] italic">No customers match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
            data-testid={testid}
            className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
      <option value="">— any —</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  </div>
);

function CalendarView({ summary }) {
  const max = Math.max(...summary.per_day.map(d => d.count), 1);
  const [openFs, setOpenFs] = useState(null);
  // Iter 167 · lazy-fetch the customer list for the expanded field staff.
  const [expandedRows, setExpandedRows] = useState({}); // { field_staff_id: [customers] | "loading" | "error" }

  // Build the row list from the AUTHORITATIVE /summary endpoint (not the
  // filtered 500-row matrix) so counts match reality.
  const grouped = useMemo(() => {
    const rows = (summary.per_field_staff || []).map(fs => ({
      id: fs.id, name: fs.name, count: fs.count,
    }));
    if (summary.unassigned > 0) {
      rows.push({ id: "__unassigned__", name: "Unassigned", count: summary.unassigned, unassigned: true });
    }
    return rows;
  }, [summary]);

  const expand = async (row) => {
    const isOpen = openFs === row.id;
    if (isOpen) { setOpenFs(null); return; }
    setOpenFs(row.id);
    // Cache: don't re-fetch if we already have the list.
    if (expandedRows[row.id] && expandedRows[row.id] !== "error") return;
    setExpandedRows(prev => ({ ...prev, [row.id]: "loading" }));
    try {
      const params = new URLSearchParams();
      if (row.unassigned) params.set("unassigned", "true");
      else params.set("field_staff_id", row.id);
      // Iter 168 — pass an explicit limit so the list matches the badge count
      // (default is 500). Cap at 5000 to keep the payload sane.
      params.set("limit", String(Math.min(Math.max(row.count || 500, 500), 5000)));
      const r = await api.get(`/operations/assignments/matrix?${params}`);
      setExpandedRows(prev => ({ ...prev, [row.id]: r.data.customers || [] }));
    } catch (e) {
      setExpandedRows(prev => ({ ...prev, [row.id]: "error" }));
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-3">
        Day-of-week service load
      </div>
      <div className="grid grid-cols-7 gap-2">
        {summary.per_day.map(d => (
          <div key={d.day} data-testid={`day-tile-${d.day}`}
               className="border border-[#E5E2D9] rounded p-3 text-center">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">{d.day}</div>
            <div className="font-heading text-2xl my-1">{d.count}</div>
            <div className="h-1 bg-[#E5E2D9] rounded overflow-hidden">
              <div style={{ width: `${(d.count / max) * 100}%` }}
                   className="h-full bg-[#2D5A27]"/>
            </div>
          </div>
        ))}
      </div>

      {/* Iter 167 — per-field-staff totals from /summary (authoritative),
          customer list lazy-fetched from /matrix on expand. */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-2">
          Per field staff · click to expand customer list
        </div>
        <div className="border border-[#E5E2D9] rounded divide-y divide-[#F0ECE3]">
          {grouped.map(row => {
            const isOpen = openFs === row.id;
            const data = expandedRows[row.id];
            return (
              <div key={row.id} data-testid={`cal-fs-${row.id}`}>
                <button onClick={() => expand(row)}
                        data-testid={`cal-fs-toggle-${row.id}`}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#F7F6F2] text-left">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>▸</span>
                    <span className={`font-medium ${row.unassigned ? "italic text-[#8A8780]" : ""}`}>{row.name}</span>
                  </div>
                  <span className="text-xs font-semibold bg-[#F7F6F2] px-2 py-0.5 rounded-full">
                    {row.count} customer{row.count === 1 ? "" : "s"}
                  </span>
                </button>
                {isOpen && (
                  <div className="bg-[#FAF9F5] px-4 py-2"
                       data-testid={`cal-fs-list-${row.id}`}>
                    {data === "loading" && (
                      <div className="text-xs text-[#8A8780] italic py-2">Loading customers…</div>
                    )}
                    {data === "error" && (
                      <div className="text-xs text-[#B42828] italic py-2">Failed to load.</div>
                    )}
                    {Array.isArray(data) && data.length === 0 && (
                      <div className="text-xs text-[#8A8780] italic py-2">No customers.</div>
                    )}
                    {Array.isArray(data) && data.length > 0 && (
                      <>
                        {data.length < row.count && (
                          <div className="text-[10px] italic text-[#8A8780] py-1">
                            Showing first {data.length} of {row.count} customers.
                          </div>
                        )}
                        <div className="divide-y divide-[#F0ECE3]">
                        {data.map(c => (
                          <div key={c.id} className="flex justify-between py-1.5 text-[12px]">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{c.name}</div>
                              <div className="text-[10px] text-[#8A8780]">
                                {c.phone} {c.customer_code && <>· {c.customer_code}</>}
                              </div>
                            </div>
                            <div className="text-[10px] text-[#5C5A55] whitespace-nowrap">
                              {(c.service_days || []).map(d => d.toUpperCase()).join(" · ") || "no days"}
                            </div>
                          </div>
                        ))}
                      </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {grouped.length === 0 && (
            <div className="p-4 text-center text-xs text-[#8A8780] italic">
              No customers grouped yet.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <MiniTable title="Per field staff" rows={summary.per_field_staff.slice(0, 10)} keyFn={r => r.id} label={r => r.name} count={r => r.count}/>
        <MiniTable title="Per office staff" rows={summary.per_office_staff.slice(0, 10)} keyFn={r => r.id} label={r => r.name} count={r => r.count}/>
        <MiniTable title="Per vehicle" rows={summary.per_vehicle.slice(0, 10)} keyFn={r => r.id} label={r => r.no} count={r => r.count}/>
      </div>
    </div>
  );
}

function MiniTable({ title, rows, keyFn, label, count }) {
  return (
    <div className="border border-[#E5E2D9] rounded">
      <div className="px-3 py-2 bg-[#F7F6F2] text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold">{title}</div>
      <div className="divide-y divide-[#F0ECE3]">
        {rows.map(r => (
          <div key={keyFn(r)} className="flex justify-between px-3 py-1.5 text-[13px]">
            <span className="truncate">{label(r)}</span>
            <span className="font-semibold">{count(r)}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="p-3 text-xs text-[#8A8780] italic text-center">Nothing yet.</div>}
      </div>
    </div>
  );
}

function AssignModal({ selectedIds, staff, officeUsers, vehicles, onClose, onDone }) {
  const [fs, setFs] = useState(""); const [os, setOs] = useState("");
  const [vid, setVid] = useState(""); const [days, setDays] = useState([]);
  const [reason, setReason] = useState(""); const [busy, setBusy] = useState(false);
  const toggleDay = (d) => setDays(days.includes(d) ? days.filter(x => x !== d) : [...days, d]);
  const submit = async () => {
    if (!fs && !os && !vid && days.length === 0) {
      toast.error("Set at least one field to assign"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/operations/assignments/bulk", {
        customer_ids: selectedIds,
        responsible_field_staff_id: fs || null,
        responsible_office_staff_id: os || null,
        assigned_vehicle_id: vid || null,
        service_days: days,
        reason: reason.trim() || null,
      });
      toast.success(`Assigned ${r.data.updated} customer${r.data.updated === 1 ? "" : "s"}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
         data-testid="assign-modal">
      <div className="bg-white rounded-lg border border-[#E5E2D9] w-full max-w-lg p-5 space-y-4"
           onClick={e => e.stopPropagation()}>
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#2D5A27] font-semibold">
            Bulk assignment
          </div>
          <div className="font-heading text-lg mt-1">{selectedIds.length} customer{selectedIds.length === 1 ? "" : "s"}</div>
        </div>
        <PickerSelect label="Field staff" value={fs} onChange={setFs}
                       options={staff.map(s => ({ id: s.id, label: s.name }))} testid="assign-fs"/>
        <PickerSelect label="Office staff" value={os} onChange={setOs}
                       options={officeUsers.map(s => ({ id: s.id, label: s.name || s.email }))} testid="assign-os"/>
        <PickerSelect label="Vehicle" value={vid} onChange={setVid}
                       options={vehicles.map(v => ({ id: v.id, label: v.vehicle_no || v.registration_no || v.number || "—" }))} testid="assign-veh"/>
        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Service days</label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {DAYS.map(d => (
              <button key={d.code} onClick={() => toggleDay(d.code)}
                      data-testid={`assign-day-${d.code}`}
                      className={`px-3 h-8 rounded-full border text-xs font-medium ${
                        days.includes(d.code)
                          ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                          : "border-[#E5E2D9] bg-white text-[#5C5A55]"
                      }`}>{d.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Reason (optional)</label>
          <input value={reason} onChange={e => setReason(e.target.value)}
                 placeholder="e.g. Initial route plan Aug 2026"
                 data-testid="assign-reason"
                 className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded border border-[#E5E2D9] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy}
                  data-testid="assign-submit"
                  className="h-9 px-4 rounded bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium">
            {busy ? "Saving…" : `Assign ${selectedIds.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const PickerSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
            data-testid={testid}
            className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
      <option value="">— don&apos;t change —</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  </div>
);

function HistoryModal({ customerId, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/operations/assignments/history/${customerId}`)
       .then(r => setRows(r.data.history || []))
       .catch(() => setRows([]));
  }, [customerId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
         data-testid="history-modal">
      <div className="bg-white rounded-lg border border-[#E5E2D9] w-full max-w-2xl max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#E5E2D9]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold">Assignment history</div>
          <div className="font-heading text-lg mt-1">Customer</div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {rows === null && <div className="text-sm text-[#8A8780] italic">Loading…</div>}
          {rows && rows.length === 0 && <div className="text-sm text-[#8A8780] italic">No changes yet.</div>}
          {rows && rows.map(h => (
            <div key={h.id} className="border border-[#E5E2D9] rounded p-3 text-sm"
                 data-testid={`hist-row-${h.id}`}>
              <div className="flex justify-between items-center">
                <span className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full ${
                  h.kind === "permanent" ? "bg-[#EFF5EC] text-[#1A3317]"
                  : h.kind === "temporary" ? "bg-[#FDF6E4] text-[#5A4600]"
                  : "bg-[#DDE7F1] text-[#1F3A57]"
                }`}>{h.kind}</span>
                <span className="text-[10px] text-[#8A8780]">{new Date(h.ts).toLocaleString()}</span>
              </div>
              <div className="text-[11px] mt-2 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[9px] uppercase text-[#8A8780]">Before</div>
                  <div>Field: {h.before?.field_staff_name || "—"}</div>
                  <div>Office: {h.before?.office_staff_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-[#8A8780]">After</div>
                  <div>Field: {h.after?.field_staff_name || "—"}</div>
                  <div>Office: {h.after?.office_staff_name || "—"}</div>
                </div>
              </div>
              {h.apply_date && <div className="text-[11px] mt-1 text-[#5C5A55]">Applies to date: <strong>{h.apply_date}</strong></div>}
              {h.reason && <div className="text-[11px] italic text-[#5C5A55] mt-1">&ldquo;{h.reason}&rdquo;</div>}
              <div className="text-[10px] text-[#8A8780] mt-1">by {h.changed_by_name}</div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-[#E5E2D9] text-right">
          <button onClick={onClose} className="h-9 px-3 rounded border border-[#E5E2D9] text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Iter 166 · Map view — every assigned customer as a colored dot
// (one hue per Field Staff). Ops can spot geographic gaps and
// clusters at a glance.
// ============================================================
const PALETTE = ["#2D5A27", "#0B2B66", "#7A2E18", "#D4A017", "#4A1976",
                  "#1F3A57", "#5A4600", "#B42828", "#3A7033", "#DB2777"];

function _custCoords(c) {
  const ll = c.last_location || c.location || {};
  // Iter 180.1 — prefer the canonical `latitude`/`longitude` fields
  // (2446 / 2450 customers) so quick-edited GPS shows up on the map.
  const lat = c.latitude ?? ll.lat ?? ll.latitude ?? c.lat;
  const lng = c.longitude ?? ll.lng ?? ll.longitude ?? c.lng;
  const nlat = Number(lat), nlng = Number(lng);
  if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null;
  // Africa/0,0 sentinel — treat as missing so we don't cluster over Ghana.
  if (Math.abs(nlat) < 0.1 && Math.abs(nlng) < 0.1) return null;
  return [nlat, nlng];
}

function MapView({ customers, staff, officeUsers, filters, setFilters }) {
  const setF = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  const clearFilters = () => setFilters(prev => ({
    ...prev, field_staff_id: "", office_staff_id: "", day: "",
  }));
  const activeCount = [filters.field_staff_id, filters.office_staff_id, filters.day]
    .filter(Boolean).length;

  const colorFor = useMemo(() => {
    const map = new Map();
    let i = 0;
    for (const s of staff) map.set(s.id, PALETTE[i++ % PALETTE.length]);
    return (fid) => (fid && map.get(fid)) || "#8A8780";
  }, [staff]);

  const points = useMemo(() =>
    customers.map(c => ({ c, coord: _custCoords(c) })).filter(p => p.coord)
  , [customers]);

  const center = points.length ? points[0].coord : [10.85, 76.27]; // Kerala fallback
  const legend = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const p of points) {
      const fid = p.c.responsible_field_staff_id;
      if (fid && !seen.has(fid)) {
        seen.add(fid);
        rows.push({
          id: fid, name: p.c.responsible_field_staff_name || fid,
          color: colorFor(fid),
          count: customers.filter(x => x.responsible_field_staff_id === fid).length,
        });
      }
    }
    rows.sort((a, b) => b.count - a.count);
    return rows;
  }, [points, customers, colorFor]);
  const noStaffCount = customers.filter(c => !c.responsible_field_staff_id).length;

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden"
         data-testid="assign-map">
      {/* Iter 180 — inline filters (mirror parent state) */}
      <div className="flex flex-wrap items-end gap-2 p-3 border-b border-[#E5E2D9]"
            data-testid="map-view-filters">
        <FilterSelect label="Field staff" value={filters.field_staff_id || ""}
                       onChange={v => setF("field_staff_id", v)}
                       options={staff.map(s => ({ id: s.id, label: s.name }))}
                       testid="map-f-fs"/>
        <FilterSelect label="Office staff" value={filters.office_staff_id || ""}
                       onChange={v => setF("office_staff_id", v)}
                       options={officeUsers.map(s => ({ id: s.id, label: s.name || s.email }))}
                       testid="map-f-os"/>
        <FilterSelect label="Assigned day" value={filters.day || ""}
                       onChange={v => setF("day", v)}
                       options={DAYS.map(d => ({ id: d.code, label: d.label }))}
                       testid="map-f-day"/>
        {activeCount > 0 && (
          <button onClick={clearFilters} data-testid="map-f-clear"
                   className="h-9 px-3 rounded border border-[#E5E2D9] text-xs">
            Clear ({activeCount})
          </button>
        )}
        <div className="ml-auto text-[11px] text-[#8A8780]">
          {customers.length} customer(s) · {points.length} on map
          {customers.length > points.length && (
            <span className="ml-2 text-[#B31A1A]">
              · {customers.length - points.length} without GPS
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4">
        <div className="md:col-span-3" style={{ height: 560 }}>
          {points.length === 0
            ? <div className="h-full flex items-center justify-center text-sm text-[#8A8780] italic">
                No customers have GPS coordinates in the current filter.
              </div>
            : <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OSM'/>
                {points.map(({ c, coord }) => (
                  <CircleMarker key={c.id} center={coord} radius={7}
                                pathOptions={{
                                  color: colorFor(c.responsible_field_staff_id),
                                  fillColor: colorFor(c.responsible_field_staff_id),
                                  fillOpacity: 0.75, weight: 1,
                                }}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold">{c.name}</div>
                        <div className="text-[10px] text-[#5C5A55]">{c.phone} · {c.customer_code}</div>
                        <div className="mt-1">Field: <strong>{c.responsible_field_staff_name || "—"}</strong></div>
                        <div>Office: <strong>{c.responsible_office_staff_name || "—"}</strong></div>
                        <div>Vehicle: <strong>{c.assigned_vehicle_no || "—"}</strong></div>
                        <div className="text-[10px] mt-1">
                          {(c.service_days || []).map(d => d.toUpperCase()).join(" · ") || "no days"}
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>}
        </div>
        <div className="border-l border-[#E5E2D9] p-3 space-y-1 max-h-[560px] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-2">
            Field staff legend
          </div>
          {legend.map(l => (
            <div key={l.id} className="flex items-center gap-2 text-[12px]">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: l.color }}/>
              <span className="flex-1 truncate">{l.name}</span>
              <span className="font-semibold">{l.count}</span>
            </div>
          ))}
          {noStaffCount > 0 && (
            <div className="flex items-center gap-2 text-[12px] pt-2 border-t border-[#F0ECE3] mt-2">
              <span className="inline-block w-3 h-3 rounded-full bg-[#8A8780]"/>
              <span className="flex-1 truncate italic text-[#8A8780]">Unassigned</span>
              <span className="font-semibold">{noStaffCount}</span>
            </div>
          )}
          {legend.length === 0 && noStaffCount === 0 && (
            <div className="text-xs text-[#8A8780] italic">No data.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function RouteBuilder({ staff, vehicles, dropLocations }) {
  const [fs, setFs] = useState(""); const [vid, setVid] = useState("");
  const [day, setDay] = useState(""); const [date, setDate] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [result, setResult] = useState(null); const [busy, setBusy] = useState(false);
  // Iter 167 — checkbox: also create a Service Request for every picked customer
  const [createSr, setCreateSr] = useState(true);
  // Iter 168 — start location picker.
  //   startMode: "auto" (staff last GPS)
  //            | "drop" (saved drop-location, uses startDropId)
  //            | "gps"  (browser geolocation)
  //            | "manual" (paste lat/lng)
  const [startMode, setStartMode] = useState("auto");
  const [startDropId, setStartDropId] = useState("");
  const [startLat, setStartLat] = useState("");
  const [startLng, setStartLng] = useState("");
  const [startLabel, setStartLabel] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);

  const fetchBrowserGps = () => {
    if (!navigator.geolocation) {
      toast.error("This browser does not expose GPS."); return;
    }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStartLat(String(pos.coords.latitude.toFixed(6)));
        setStartLng(String(pos.coords.longitude.toFixed(6)));
        setStartLabel("My current location");
        setGpsBusy(false);
        toast.success("Captured browser GPS");
      },
      (err) => {
        setGpsBusy(false);
        toast.error(`GPS unavailable: ${err.message || "permission denied"}`);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const resolveStart = () => {
    if (startMode === "drop") {
      const d = (dropLocations || []).find(x => x.id === startDropId);
      if (!d) return { error: "Pick a saved drop location" };
      return { start_lat: d.latitude, start_lng: d.longitude, start_label: d.name };
    }
    if (startMode === "gps" || startMode === "manual") {
      const la = parseFloat(startLat), ln = parseFloat(startLng);
      if (!Number.isFinite(la) || !Number.isFinite(ln)) return { error: "Enter a valid lat and lng" };
      return { start_lat: la, start_lng: ln, start_label: startLabel || (startMode === "gps" ? "Browser GPS" : "Manual") };
    }
    return {}; // auto — let backend fall back to staff last GPS
  };

  const loadRoster = async () => {
    if (!fs && !vid && !day) { toast.error("Pick at least one filter"); return; }
    setBusy(true); setResult(null);
    try {
      const q = new URLSearchParams();
      if (fs) q.set("field_staff_id", fs);
      if (vid) q.set("vehicle_id", vid);
      if (day) q.set("day", day);
      const r = await api.get(`/operations/assignments/matrix?${q}`);
      const list = r.data.customers || [];
      setCustomers(list);
      setChecked(new Set(list.map(c => c.id))); // all checked
      setLoaded(true);
      if (list.length === 0) {
        toast.info("No customers assigned for this staff/day yet.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const toggle = (id) => {
    const n = new Set(checked); if (n.has(id)) n.delete(id); else n.add(id); setChecked(n);
  };
  const optimize = async () => {
    if (!date) { toast.error("Pick a target date"); return; }
    if (checked.size === 0) { toast.error("Select at least one customer"); return; }
    const startPayload = resolveStart();
    if (startPayload.error) { toast.error(startPayload.error); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/operations/assignments/build-route", {
        field_staff_id: fs || null, vehicle_id: vid || null,
        day_of_week: day || null, date,
        customer_ids: [...checked],
        create_service_requests: createSr,
        ...startPayload,
      });
      setResult(r.data);
      // Reorder customers to reflect optimized sequence.
      const map = new Map(customers.map(c => [c.id, c]));
      setCustomers(r.data.ordered_customer_ids.map(id => map.get(id)).filter(Boolean));
      const srMsg = r.data.srs_created > 0
        ? ` · ${r.data.srs_created} SR${r.data.srs_created === 1 ? "" : "s"} created`
        : "";
      const startMsg = startPayload.start_label ? ` · starts from ${startPayload.start_label}` : "";
      toast.success(`Route optimized · ${r.data.tasks_created} task${r.data.tasks_created === 1 ? "" : "s"} pushed${srMsg}${startMsg}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4"
         data-testid="route-builder">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end mb-3">
        <FilterSelect label="Field staff" value={fs} onChange={setFs}
                       options={staff.map(s => ({ id: s.id, label: s.name }))} testid="rb-fs"/>
        <FilterSelect label="Vehicle" value={vid} onChange={setVid}
                       options={vehicles.map(v => ({ id: v.id, label: v.vehicle_no || v.registration_no || v.number || "—" }))} testid="rb-veh"/>
        <FilterSelect label="Day of week" value={day} onChange={setDay}
                       options={DAYS.map(d => ({ id: d.code, label: d.label }))} testid="rb-day"/>
        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Target date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 data-testid="rb-date"
                 className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
      </div>

      {/* Iter 168 — start location picker */}
      <div className="border border-[#E5E2D9] rounded-md p-3 mb-3 bg-[#FAF9F5]"
           data-testid="rb-start-picker">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold mb-2">
          Route starts from
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {[
            { code: "auto", label: "Auto (staff last GPS)" },
            { code: "drop", label: "Saved location" },
            { code: "gps",  label: "My browser GPS" },
            { code: "manual", label: "Custom lat / lng" },
          ].map(m => (
            <button key={m.code} onClick={() => setStartMode(m.code)}
                    data-testid={`rb-start-mode-${m.code}`}
                    className={`px-3 h-8 rounded-full border text-xs font-medium ${
                      startMode === m.code
                        ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                        : "border-[#E5E2D9] bg-white text-[#5C5A55]"
                    }`}>{m.label}</button>
          ))}
        </div>
        {startMode === "drop" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <FilterSelect label="Saved drop / godown" value={startDropId}
                           onChange={setStartDropId}
                           options={(dropLocations || []).map(d => ({
                             id: d.id,
                             label: `${d.name} (${(d.latitude || 0).toFixed(4)}, ${(d.longitude || 0).toFixed(4)})`,
                           }))}
                           testid="rb-start-drop"/>
            {(!dropLocations || dropLocations.length === 0) && (
              <div className="text-[11px] text-[#8A8780] italic self-end">
                No drop locations saved. Add them under Masters → Drop Locations.
              </div>
            )}
          </div>
        )}
        {startMode === "gps" && (
          <div className="flex flex-wrap items-end gap-2">
            <button onClick={fetchBrowserGps} disabled={gpsBusy}
                    data-testid="rb-start-gps-btn"
                    className="h-9 px-3 rounded border border-[#2D5A27] text-[#2D5A27] text-xs font-semibold bg-white disabled:opacity-60">
              {gpsBusy ? "Locating…" : "Get browser GPS"}
            </button>
            {startLat && startLng && (
              <span className="text-[11px] text-[#2D5A27] font-medium"
                    data-testid="rb-start-gps-value">
                ✓ {parseFloat(startLat).toFixed(6)}, {parseFloat(startLng).toFixed(6)}
              </span>
            )}
          </div>
        )}
        {startMode === "manual" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Latitude</label>
              <input value={startLat} onChange={e => setStartLat(e.target.value)}
                     placeholder="10.85"
                     data-testid="rb-start-lat"
                     className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Longitude</label>
              <input value={startLng} onChange={e => setStartLng(e.target.value)}
                     placeholder="76.27"
                     data-testid="rb-start-lng"
                     className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Label (optional)</label>
              <input value={startLabel} onChange={e => setStartLabel(e.target.value)}
                     placeholder="e.g. Aluva depot"
                     data-testid="rb-start-label"
                     className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={loadRoster} disabled={busy}
                data-testid="rb-load"
                className="h-9 px-3 rounded border border-[#E5E2D9] text-sm inline-flex items-center gap-1">
          <ArrowsClockwise size={12}/>Load roster
        </button>
        <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-[#E5E2D9] text-xs cursor-pointer bg-[#FAF9F5]"
               data-testid="rb-create-sr-label">
          <input type="checkbox" checked={createSr}
                 onChange={e => setCreateSr(e.target.checked)}
                 data-testid="rb-create-sr"
                 className="accent-[#2D5A27]"/>
          Create SR for every selected customer
        </label>
        <button onClick={optimize} disabled={busy || customers.length === 0}
                data-testid="rb-optimize"
                className="h-9 px-3 rounded bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-1">
          <Path size={12} weight="fill"/>
          Optimize & push {checked.size > 0 && `(${checked.size})`}
        </button>
        {result && (
          <span className="text-[11px] text-[#2D5A27] inline-flex items-center gap-1 flex-wrap">
            <CheckCircle size={12} weight="fill"/>
            {result.tasks_created} task{result.tasks_created === 1 ? "" : "s"} · {result.total_km ?? "—"} km
            {result.total_traffic_min != null
              ? <span className="text-[#7A2E18] font-semibold">
                  · ~{Math.round(result.total_traffic_min)} min live traffic
                </span>
              : result.total_min != null
                ? <span> · ~{Math.round(result.total_min)} min</span>
                : null}
            {result.route_source && (
              <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780] font-normal ml-1">
                via {result.route_source}
              </span>
            )}
          </span>
        )}
      </div>
      <div className="border border-[#E5E2D9] rounded max-h-[520px] overflow-y-auto"
           data-testid="rb-list">
        {customers.length === 0 && !loaded && (
          <div className="p-6 text-center text-sm text-[#8A8780] italic">
            Pick filters and click <strong>Load roster</strong>.
          </div>
        )}
        {customers.length === 0 && loaded && (
          <div className="p-6 text-center text-sm text-[#8A8780] italic"
               data-testid="rb-empty">
            No customers assigned for this staff/day yet. Try different filters
            or use the Assignment list tab to assign customers first.
          </div>
        )}
        {customers.map((c, i) => (
          <label key={c.id}
                 data-testid={`rb-row-${c.id}`}
                 className="flex items-center gap-2 px-3 py-2 border-b border-[#F0ECE3] last:border-b-0 hover:bg-[#F7F6F2] cursor-pointer">
            <input type="checkbox" checked={checked.has(c.id)}
                   onChange={() => toggle(c.id)}
                   className="accent-[#2D5A27]"/>
            {result && (
              <span className="w-6 h-6 rounded-full bg-[#2D5A27] text-white flex items-center justify-center text-[11px] font-bold">
                {i + 1}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[11px] text-[#5C5A55]">
                {c.phone} · {(c.service_days || []).map(d => d.toUpperCase()).join(",")}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// Iter 180 — Customer Roster tab. Filterable table with GPS status,
// package + on/off toggle. Quick-edit modals for GPS + package. CSV
// and PDF export via /operations/assignments/roster/export.
function RosterView({ customers, staff, officeUsers, filters, setFilters, onRefresh }) {
  const [packages, setPackages] = useState([]);
  const [edit, setEdit] = useState(null); // {mode:"gps"|"package"|"status", cust}
  useEffect(() => {
    api.get("/packages").then(r => setPackages(r.data || []))
       .catch(() => setPackages([]));
  }, []);

  const isInactive = (c) => c.status === "inactive";
  const filtered = useMemo(() => customers.filter(c => {
    if (filters.status === "active"   && isInactive(c)) return false;
    if (filters.status === "inactive" && !isInactive(c)) return false;
    return true;
  }), [customers, filters.status]);

  const hasValidGps = (c) => {
    const lat = c.latitude ?? c.location?.lat ?? c.last_location?.lat;
    const lng = c.longitude ?? c.location?.lng ?? c.last_location?.lng;
    if (typeof lat !== "number" || typeof lng !== "number") return false;
    return Math.abs(lat) > 0.1 || Math.abs(lng) > 0.1;
  };

  const download = async (fmt) => {
    const params = new URLSearchParams({ fmt });
    if (filters.field_staff_id) params.set("field_staff_id", filters.field_staff_id);
    if (filters.office_staff_id) params.set("office_staff_id", filters.office_staff_id);
    if (filters.day) params.set("day", filters.day);
    if (filters.status) params.set("status", filters.status);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/operations/assignments/roster/export?${params}`;
    try {
      const token = localStorage.getItem("rescigo_token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const msg = await r.text().catch(() => "");
        throw new Error(`Export failed (${r.status}) ${msg.slice(0, 200)}`);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fmt === "pdf" ? "customer-roster.pdf" : "customer-roster.csv";
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(e?.message || formatApiError(e)); }
  };

  const toggleStatus = async (c) => {
    // Only allow simple active⇄inactive toggle. Non-lifecycle statuses
    // ('approved', 'pending_verification', 'rejected') must be changed
    // from the customer profile page — silently overwriting them here
    // would destroy verification state.
    if (c.status && c.status !== "active" && c.status !== "inactive") {
      toast.error(`Cannot toggle from '${c.status}' — open the customer profile.`);
      return;
    }
    const next = c.status === "inactive" ? "active" : "inactive";
    try {
      await api.patch(`/operations/assignments/customers/${c.id}/quick-edit`,
                       { status: next });
      toast.success(`${c.name} → ${next}`);
      onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-2 mb-3" data-testid="roster-toolbar">
        <FilterSelect label="Field staff" value={filters.field_staff_id || ""}
                      onChange={v => setFilters(f => ({ ...f, field_staff_id: v }))}
                      options={staff.map(s => ({ id: s.id, label: s.name }))}
                      testid="roster-f-fs"/>
        <FilterSelect label="Office staff" value={filters.office_staff_id || ""}
                      onChange={v => setFilters(f => ({ ...f, office_staff_id: v }))}
                      options={officeUsers.map(s => ({ id: s.id, label: s.name || s.email }))}
                      testid="roster-f-os"/>
        <FilterSelect label="Service day" value={filters.day || ""}
                      onChange={v => setFilters(f => ({ ...f, day: v }))}
                      options={[
                        { id: "mon", label: "Monday" }, { id: "tue", label: "Tuesday" },
                        { id: "wed", label: "Wednesday" }, { id: "thu", label: "Thursday" },
                        { id: "fri", label: "Friday" }, { id: "sat", label: "Saturday" },
                        { id: "sun", label: "Sunday" },
                      ]} testid="roster-f-day"/>
        <FilterSelect label="Status" value={filters.status || ""}
                      onChange={v => setFilters(f => ({ ...f, status: v }))}
                      options={[
                        { id: "active", label: "Active" },
                        { id: "inactive", label: "Inactive" },
                      ]} testid="roster-f-status"/>
        <div className="ml-auto flex gap-2">
          <button onClick={() => download("csv")} data-testid="roster-export-csv"
                  className="h-9 px-3 rounded border border-[#E5E2D9] text-xs">Export CSV</button>
          <button onClick={() => download("pdf")} data-testid="roster-export-pdf"
                  className="h-9 px-3 rounded bg-[#2D5A27] text-white text-xs font-medium">Export PDF</button>
        </div>
      </div>
      <div className="text-[11px] text-[#8A8780] mb-2">{filtered.length} customer(s)</div>

      {/* Table */}
      <div className="overflow-x-auto border border-[#F0ECE3] rounded">
        <table className="min-w-full text-[12px]">
          <thead className="bg-[#FAF9F5] text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
            <tr>
              <th className="p-2 text-left">Customer</th>
              <th className="p-2 text-left">Phone</th>
              <th className="p-2 text-left">Field staff</th>
              <th className="p-2 text-left">Days</th>
              <th className="p-2 text-left">GPS</th>
              <th className="p-2 text-left">Package</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0ECE3]">
            {filtered.map(c => {
              const gpsOk = hasValidGps(c);
              return (
                <tr key={c.id} data-testid={`roster-row-${c.id}`}>
                  <td className="p-2">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-[10px] text-[#8A8780]">{c.customer_code}</div>
                  </td>
                  <td className="p-2">{c.phone}</td>
                  <td className="p-2">{c.responsible_field_staff_name || <span className="italic text-[#8A8780]">—</span>}</td>
                  <td className="p-2 text-[10px] uppercase">{(c.service_days || []).join(" · ") || "—"}</td>
                  <td className="p-2">
                    <button onClick={() => setEdit({ mode: "gps", cust: c })}
                            data-testid={`roster-gps-${c.id}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
                              gpsOk ? "bg-[#DCEEDC] text-[#1A3317]" : "bg-[#FCE7E7] text-[#B31A1A] border border-[#E42020]"
                            }`}>
                      {gpsOk ? "✓ OK" : "✎ Add GPS"}
                    </button>
                  </td>
                  <td className="p-2">
                    <button onClick={() => setEdit({ mode: "package", cust: c })}
                            data-testid={`roster-pkg-${c.id}`}
                            className="text-[11px] underline text-[#2D5A27]">
                      {c.package_name || <span className="italic text-[#8A8780]">— set</span>}
                    </button>
                  </td>
                  <td className="p-2">
                    {(() => {
                      const s = c.status || "active";
                      const isLifecycle = s !== "active" && s !== "inactive";
                      const cls = s === "inactive"
                        ? "bg-[#F7F6F2] text-[#8A8780]"
                        : isLifecycle
                          ? "bg-[#FFF3D6] text-[#7A5B00] border border-[#E7C356]"
                          : "bg-[#DCEEDC] text-[#1A3317]";
                      return (
                        <button onClick={() => toggleStatus(c)}
                                 data-testid={`roster-status-${c.id}`}
                                 title={isLifecycle ? "Open customer profile to change lifecycle status" : "Toggle active / inactive"}
                                 className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`}>
                          {s.toUpperCase().replace("_", " ")}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-xs text-[#8A8780] italic">
                No customers match the current filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {edit && (
        <RosterEditModal edit={edit} packages={packages}
                          onClose={() => setEdit(null)}
                          onDone={() => { setEdit(null); onRefresh(); }}/>
      )}
    </div>
  );
}

function RosterEditModal({ edit, packages, onClose, onDone }) {
  const { mode, cust } = edit;
  const [lat, setLat] = useState(cust.latitude || "");
  const [lng, setLng] = useState(cust.longitude || "");
  const [pkg, setPkg] = useState(cust.package_id || "");
  const [busy, setBusy] = useState(false);

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not available"); return; }
    navigator.geolocation.getCurrentPosition(
      p => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); },
      e => toast.error(`GPS: ${e.message}`),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = mode === "gps"
        ? { latitude: Number(lat), longitude: Number(lng) }
        : mode === "package" ? { package_id: pkg || null }
        : {};
      await api.patch(`/operations/assignments/customers/${cust.id}/quick-edit`, body);
      toast.success("Saved");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
         onClick={onClose} data-testid="roster-edit-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="font-heading text-lg mb-1">
          {mode === "gps" ? "Update GPS" : "Change Package"}
        </div>
        <div className="text-xs text-[#8A8780] mb-4">{cust.name}</div>
        {mode === "gps" && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div><label className="text-[10px] uppercase text-[#5C5A55] font-semibold">Latitude</label>
                <input value={lat} onChange={e => setLat(e.target.value)}
                       data-testid="roster-edit-lat"
                       className="w-full h-9 mt-1 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/></div>
              <div><label className="text-[10px] uppercase text-[#5C5A55] font-semibold">Longitude</label>
                <input value={lng} onChange={e => setLng(e.target.value)}
                       data-testid="roster-edit-lng"
                       className="w-full h-9 mt-1 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/></div>
            </div>
            <button onClick={captureGps} data-testid="roster-edit-gps-btn"
                    className="w-full h-9 border border-[#2D5A27] text-[#2D5A27] rounded text-xs font-semibold mb-3">
              Use my browser GPS
            </button>
          </>
        )}
        {mode === "package" && (
          <div className="mb-3">
            <label className="text-[10px] uppercase text-[#5C5A55] font-semibold">Package</label>
            <select value={pkg} onChange={e => setPkg(e.target.value)}
                    data-testid="roster-edit-pkg"
                    className="w-full h-9 mt-1 px-2 border border-[#E5E2D9] rounded text-sm">
              <option value="">— None</option>
              {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="h-9 px-3 border border-[#E5E2D9] rounded text-xs">Cancel</button>
          <button onClick={save} disabled={busy}
                  data-testid="roster-edit-save"
                  className="h-9 px-4 bg-[#2D5A27] text-white rounded text-xs font-semibold disabled:opacity-60">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Iter 191 — Assigned Daily Duty Roster
// Shows every field staff and the customers pushed to them for a day.
// ═════════════════════════════════════════════════════════════════════
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtHm = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }); }
  catch { return iso; }
};

function DutyRosterView() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [reassignFor, setReassignFor] = useState(null); // task obj
  const [staffList, setStaffList] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/operations/assignments/duty-roster?date=${date}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => {
    api.get("/staff").then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setStaffList(arr);
    }).catch(() => {});
  }, []);

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("rescigo_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/operations/assignments/duty-roster/export?date=${date}&fmt=${fmt}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fmt === "pdf" ? `duty-roster-${date}.pdf` : `duty-roster-${date}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(e?.message || formatApiError(e)); }
  };

  const doReassign = async (toStaffId) => {
    if (!reassignFor) return;
    try {
      await api.post("/operations/assignments/duty-roster/reassign",
                      { task_id: reassignFor.task_id, to_staff_id: toStaffId });
      toast.success("Task reassigned");
      setReassignFor(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    const groups = data?.field_staff || [];
    const s = q.trim().toLowerCase();
    if (!s) return groups;
    // Iter 191.1 — if the search term matches the field staff name,
    // keep ALL of that staff's tasks. Otherwise filter tasks by
    // customer / phone / SR number and keep the group when it still
    // has at least one match.
    return groups
      .map(g => {
        const staffMatches = (g.field_staff_name || "").toLowerCase().includes(s);
        if (staffMatches) return g;
        return {
          ...g,
          tasks: g.tasks.filter(t =>
            (t.customer_name || "").toLowerCase().includes(s)
            || (t.customer_phone || "").toLowerCase().includes(s)
            || (t.sr_request_no || "").toLowerCase().includes(s)),
        };
      })
      .filter(g => g.tasks.length > 0);
  }, [data, q]);
  const filteredCount = useMemo(
    () => filtered.reduce((n, g) => n + g.tasks.length, 0),
    [filtered],
  );

  const statusCls = (s) => ({
    pending: "bg-[#F7F6F2] text-[#5C5A55]",
    in_progress: "bg-[#F9E4B7] text-[#7A5A11] animate-pulse",
    completed: "bg-[#DCEEDC] text-[#1A3317]",
    missed: "bg-[#F9EDE7] text-[#7A2E18]",
    skipped: "bg-[#FFF7E6] text-[#7A5A11]",
  }[s] || "bg-[#F7F6F2] text-[#5C5A55]");

  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden"
          data-testid="duty-roster">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-2 p-3 border-b border-[#E5E2D9]">
        <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
          <div className="mb-1">Date</div>
          <input type="date" value={date}
                  onChange={e => setDate(e.target.value)}
                  data-testid="duty-roster-date"
                  className="h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
        </label>
        <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold flex-1 min-w-[200px]">
          <div className="mb-1">Search</div>
          <input placeholder="Field staff · customer · SR no…"
                  value={q} onChange={e => setQ(e.target.value)}
                  data-testid="duty-roster-search"
                  className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-[#8A8780]">
            {data ? `${filtered.length} staff · ${filteredCount} pushed` : ""}
          </span>
          <button onClick={() => download("csv")}
                   data-testid="duty-roster-export-csv"
                   disabled={!data || data.count === 0}
                   className="h-8 px-3 rounded border border-[#E5E2D9] bg-white hover:bg-[#F0ECE3] disabled:opacity-50 text-[12px] font-semibold">
            CSV
          </button>
          <button onClick={() => download("pdf")}
                   data-testid="duty-roster-export-pdf"
                   disabled={!data || data.count === 0}
                   className="h-8 px-3 rounded border border-[#E5E2D9] bg-white hover:bg-[#F0ECE3] disabled:opacity-50 text-[12px] font-semibold">
            PDF
          </button>
        </div>
      </div>

      {loading && <div className="p-6 text-center italic text-[#8A8780]">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="p-6 text-center italic text-[#8A8780]">
          No field staff pushed for {date}.
        </div>
      )}

      <div className="divide-y divide-[#F0ECE3]">
        {filtered.map(g => (
          <section key={g.field_staff_id}
                    data-testid={`duty-roster-staff-${g.field_staff_id}`}
                    className="p-3">
            <header className="flex flex-wrap items-center gap-2 mb-2">
              <div className="font-heading text-base text-[#1A1A1A]">{g.field_staff_name}</div>
              {g.field_staff_code && (
                <span className="text-[10px] font-mono text-[#8A8780]">{g.field_staff_code}</span>
              )}
              {g.field_staff_phone && (
                <a href={`tel:${g.field_staff_phone}`}
                    className="text-[11px] font-mono text-[#2D5A27] hover:underline">
                  {g.field_staff_phone}
                </a>
              )}
              <span className="ml-auto text-[11px] font-mono text-[#5C5A55]">
                {g.tasks.length} customer{g.tasks.length !== 1 ? "s" : ""}
              </span>
              {Object.entries(g.counts).filter(([, v]) => v > 0).map(([k, v]) => (
                <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-[0.14em] ${statusCls(k)}`}>
                  {k.replace(/_/g, " ")} · {v}
                </span>
              ))}
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Package</th>
                    <th className="text-left p-2">SR / Priority</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Start</th>
                    <th className="text-left p-2">End</th>
                    <th className="text-left p-2">Source</th>
                    <th className="text-right p-2"> </th>
                  </tr>
                </thead>
                <tbody>
                  {g.tasks.map(t => (
                    <tr key={t.task_id}
                         data-testid={`duty-roster-row-${t.task_id}`}
                         className="border-t border-[#F0ECE3] hover:bg-[#F7F6F2]">
                      <td className="p-2 font-mono text-[#8A8780]">{t.sequence ?? "—"}</td>
                      <td className="p-2">
                        <div className="font-medium">{t.customer_name}</div>
                        {t.customer_phone && (
                          <div className="text-[10px] font-mono text-[#8A8780]">{t.customer_phone}</div>
                        )}
                      </td>
                      <td className="p-2 text-[11px]">{t.package_name || "—"}</td>
                      <td className="p-2">
                        {t.sr_request_no && (
                          <div className="font-mono text-[11px] text-[#2D5A27]">{t.sr_request_no}</div>
                        )}
                        {t.sr_priority && t.sr_priority !== "NORMAL" && (
                          <div className={`text-[9px] uppercase tracking-[0.14em] font-bold ${
                            t.sr_priority === "EMERGENCY" ? "text-[#6D28D9]"
                              : t.sr_priority === "HIGH" ? "text-[#A855F7]"
                              : "text-[#C25D38]"
                          }`}>{t.sr_priority}</div>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-[0.14em] ${statusCls(t.status)}`}>
                          {t.status.replace(/_/g, " ")}
                        </span>
                        {t.skip_reason && (
                          <div className="text-[9px] text-[#7A5A11] mt-0.5">
                            {t.skip_reason.replace(/_/g, " ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 font-mono text-[11px]">{fmtHm(t.start_time)}</td>
                      <td className="p-2 font-mono text-[11px]">{fmtHm(t.end_time)}</td>
                      <td className="p-2 text-[10px] text-[#8A8780]">
                        {(t.source || "manual").replace(/_/g, " ")}
                        {t.vehicle_no && ` · ${t.vehicle_no}`}
                      </td>
                      <td className="p-2 text-right">
                        {["pending", "in_progress", "skipped"].includes(t.status) && (
                          <button type="button"
                                   onClick={() => setReassignFor({ ...t, from_staff_name: g.field_staff_name })}
                                   data-testid={`duty-roster-reassign-${t.task_id}`}
                                   className="text-[10px] font-semibold text-[#2D5A27] hover:underline">
                            Reassign →
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>

      {reassignFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
              onClick={() => setReassignFor(null)}
              data-testid="duty-roster-reassign-modal">
          <div onClick={e => e.stopPropagation()}
                className="bg-white rounded-md w-full max-w-md border border-[#E5E2D9] shadow-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-[#F0ECE3]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#2D5A27] font-semibold">
                Reassign task
              </div>
              <div className="text-[13px] font-medium mt-0.5">
                <strong>{reassignFor.customer_name}</strong> is currently with{" "}
                <strong>{reassignFor.from_staff_name}</strong>. Pick a new field staff.
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-[#F0ECE3]">
              {staffList
                .filter(s => s.id !== reassignFor.from_staff_id)
                .map(s => (
                <button key={s.id}
                         onClick={() => doReassign(s.id)}
                         data-testid={`duty-roster-reassign-pick-${s.id}`}
                         className="w-full flex items-center gap-2 p-3 text-left hover:bg-[#F0ECE3]">
                  <div className="w-8 h-8 rounded-full bg-[#DCEEDC] text-[#1A3317] font-bold flex items-center justify-center text-[12px]">
                    {(s.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[13px] truncate">{s.name}</div>
                    {s.phone && <div className="text-[10px] font-mono text-[#8A8780]">{s.phone}</div>}
                  </div>
                </button>
              ))}
            </div>
            <div className="p-3 border-t border-[#F0ECE3] text-right">
              <button onClick={() => setReassignFor(null)}
                       data-testid="duty-roster-reassign-cancel"
                       className="h-9 px-3 rounded border border-[#E5E2D9] text-[12px] hover:bg-[#F7F6F2]">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




/* ══════════════════════════════════════════════════════════════════
 * Iter 220h — My Responsible Customers
 * Office staff → list of customers they are the Fixed Responsibility
 * office contact for, with last-SR status, dues, wallet-low flags,
 * plus LSGD / day / search / created-by-sort filters.
 * ══════════════════════════════════════════════════════════════════ */
function MyResponsibleCustomers({ officeUsers = [], locations = {} }) {
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem("rescigo_user") || "null"); }
    catch { return null; }
  })();
  const canPickAnyone = !!currentUser && [
    "superadmin", "admin", "manager", "coordinator",
  ].includes(currentUser.role);
  const [officeStaffId, setOfficeStaffId] = useState(currentUser?.id || "");
  const [q, setQ] = useState("");
  const [lsgdId, setLsgdId] = useState("");
  const [day, setDay] = useState("");
  const [sort, setSort] = useState("-last_sr_at");
  const [createdVia, setCreatedVia] = useState(""); // "" | "customer" | "office"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (officeStaffId && canPickAnyone) p.set("office_staff_id", officeStaffId);
      if (q.trim()) p.set("q", q.trim());
      if (lsgdId) p.set("lsgd_id", lsgdId);
      if (day) p.set("day", day);
      if (sort) p.set("sort", sort);
      const r = await api.get(`/operations/assignments/my-responsible-customers?${p}`);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [officeStaffId, lsgdId, day, sort]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const filteredRows = useMemo(() => {
    if (!createdVia) return rows;
    return rows.filter((r) =>
      createdVia === "customer" ? r.sr_by_customer > 0 : r.sr_by_office > 0,
    );
  }, [createdVia, rows]);

  const clearFilters = () => {
    setQ(""); setLsgdId(""); setDay(""); setSort("-last_sr_at"); setCreatedVia("");
  };

  const totals = useMemo(() => ({
    total: filteredRows.length,
    walletLow: filteredRows.filter((r) => r.wallet_low).length,
    pendingBills: filteredRows.filter((r) => r.bill_pending).length,
    overdueDues: filteredRows.filter((r) => (r.due_days ?? 0) > 15).length,
  }), [filteredRows]);

  const lsgds = locations.lsgds || [];
  const toggleSort = (key) => setSort(sort === `-${key}` ? key : `-${key}`);
  const sortIcon = (key) => sort === key
    ? <CaretUp size={11} weight="bold" className="inline ml-0.5"/>
    : sort === `-${key}` ? <CaretDown size={11} weight="bold" className="inline ml-0.5"/> : null;

  return (
    <div className="bg-white rounded-md border border-[#E5E2D9]" data-testid="my-responsible-customers-panel">
      {/* Toolbar */}
      <div className="p-4 border-b border-[#E5E2D9] space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {canPickAnyone && (
            <select value={officeStaffId} onChange={(e) => setOfficeStaffId(e.target.value)}
                    data-testid="mrc-office-staff"
                    className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white min-w-[220px]">
              <option value="">Select office staff…</option>
              {officeUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email} {u.role ? `· ${u.role.replace(/_/g," ")}` : ""}
                </option>
              ))}
            </select>
          )}
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search customer / phone / code"
                   data-testid="mrc-search"
                   className="w-full h-9 pl-8 pr-3 border border-[#E5E2D9] rounded-md text-xs bg-white"/>
          </div>
          <select value={lsgdId} onChange={(e) => setLsgdId(e.target.value)}
                  data-testid="mrc-lsgd"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">All LSGDs</option>
            {lsgds.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select value={day} onChange={(e) => setDay(e.target.value)}
                  data-testid="mrc-day"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">All assigned days</option>
            {DAYS.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
          </select>
          <select value={createdVia} onChange={(e) => setCreatedVia(e.target.value)}
                  data-testid="mrc-created-via"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">SRs by anyone</option>
            <option value="customer">Any customer-made SR</option>
            <option value="office">Any office-made SR</option>
          </select>
          {(q || lsgdId || day || createdVia || sort !== "-last_sr_at") && (
            <button onClick={clearFilters} data-testid="mrc-clear"
                    className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white text-[#5C5A55] inline-flex items-center gap-1">
              <X size={11} weight="bold"/>Clear
            </button>
          )}
          <div className="flex-1"/>
          <button onClick={load} data-testid="mrc-refresh"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white text-[#5C5A55] inline-flex items-center gap-1 hover:bg-[#F7F6F2]">
            <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""}/>Refresh
          </button>
        </div>
        {/* Stat pills */}
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F7F6F2] text-[#1A1A1A]">
            <Users size={12}/> {totals.total} customers
          </span>
          {totals.walletLow > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FEF7E6] text-[#7A5A11] font-bold" data-testid="mrc-stat-wallet-low">
              <Wallet size={12}/> {totals.walletLow} wallet &lt; ₹1000
            </span>
          )}
          {totals.pendingBills > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FEF7F6] text-[#7A2E18] font-bold" data-testid="mrc-stat-bill-pending">
              <Receipt size={12}/> {totals.pendingBills} pending bill{totals.pendingBills > 1 ? "s" : ""}
            </span>
          )}
          {totals.overdueDues > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FEE2E2] text-[#7F1D1D] font-bold">
              <Warning size={12}/> {totals.overdueDues} due &gt; 15 days
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <th className="px-4 py-2.5 cursor-pointer select-none" onClick={() => toggleSort("name")}>
                Customer{sortIcon("name")}
              </th>
              <th className="px-4 py-2.5">LSGD · Days</th>
              <th className="px-4 py-2.5 cursor-pointer select-none" onClick={() => toggleSort("last_sr_at")}
                  data-testid="mrc-sort-last-sr">
                Last SR{sortIcon("last_sr_at")}
              </th>
              <th className="px-4 py-2.5">SRs (cust / office)</th>
              <th className="px-4 py-2.5 cursor-pointer select-none text-right" onClick={() => toggleSort("wallet_balance")}
                  data-testid="mrc-sort-wallet">
                Wallet{sortIcon("wallet_balance")}
              </th>
              <th className="px-4 py-2.5 text-right">Dues</th>
              <th className="px-4 py-2.5 cursor-pointer select-none" onClick={() => toggleSort("customer_created_at")}
                  data-testid="mrc-sort-assigned">
                Assigned{sortIcon("customer_created_at")}
              </th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-[#8A8780]">
                  <Users size={28} weight="duotone" className="mx-auto mb-2 text-[#8A8780]"/>
                  {loading ? "Loading…" :
                   !officeStaffId ? "Select an office staff to see their responsible customers." :
                   "No customers match these filters."}
                </td>
              </tr>
            )}
            {filteredRows.map((r, i) => {
              // Iter 220i — attention colouring
              // • red pulse when a scheduled SR is past 2 PM without completion
              // • green pulse when this customer has not created an SR in 13d
              const rowTone = r.sr_overdue_red
                ? "mrc-blink-red bg-[#FEE2E2]"
                : r.no_recent_sr_green
                ? "mrc-blink-green bg-[#F0FDF4]"
                : (i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]");
              return (
              <tr key={r.customer_id}
                  className={rowTone}
                  data-testid={`mrc-row-${r.customer_id}`}
                  data-blink={r.sr_overdue_red ? "red" : r.no_recent_sr_green ? "green" : ""}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <div className="font-semibold text-[#1A1A1A] truncate max-w-[220px]">{r.name || "—"}</div>
                    {r.sr_overdue_red && (
                      <span data-testid={`mrc-overdue-${r.customer_id}`}
                            className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded-full bg-[#DC2626] text-white font-bold animate-pulse">
                        Overdue
                      </span>
                    )}
                    {r.no_recent_sr_green && !r.sr_overdue_red && (
                      <span data-testid={`mrc-idle-${r.customer_id}`}
                            className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded-full bg-[#15803D] text-white font-bold animate-pulse">
                        Idle {r.days_since_last_sr != null ? `${r.days_since_last_sr}d` : ">13d"}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#8A8780] font-mono">{r.phone} · {r.customer_code || "—"}</div>
                </td>
                <td className="px-4 py-2.5 text-[11px]">
                  <div className="text-[#1A1A1A] truncate max-w-[180px]">
                    {(lsgds.find((l) => l.id === r.lsgd_id) || {}).name || "—"}
                  </div>
                  <div className="text-[10px] text-[#8A8780] uppercase tracking-[0.14em]">
                    {(r.service_days || []).join(", ") || "—"}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[11px]">
                  {r.last_sr_at ? (
                    <>
                      <div className="text-[#1A1A1A] font-mono">
                        {new Date(r.last_sr_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      </div>
                      <div className="text-[10px] text-[#8A8780]">
                        {r.last_sr_no || ""} ·{" "}
                        <span className={`font-bold ${
                          r.last_sr_status === "COMPLETED" ? "text-[#15803D]"
                          : ["CANCELLED","REJECTED","DELETED"].includes(r.last_sr_status) ? "text-[#5C5A55]"
                          : "text-[#B58412]"
                        }`}>{r.last_sr_status}</span>
                      </div>
                    </>
                  ) : (<span className="text-[#8A8780] italic">No SR yet</span>)}
                </td>
                <td className="px-4 py-2.5 text-[11px]">
                  <div>
                    <span className="font-bold text-[#1A1A1A]">{r.sr_total}</span>
                    <span className="text-[10px] text-[#8A8780] ml-1">total</span>
                  </div>
                  <div className="text-[10px] text-[#5C5A55]">
                    <span className="text-[#DC2626]">{r.sr_by_customer}</span> customer ·{" "}
                    <span className="text-[#0B2B66]">{r.sr_by_office}</span> office
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className={`font-mono text-[13px] font-bold ${r.wallet_low ? "text-[#B58412]" : "text-[#15803D]"}`}>
                    ₹{Number(r.wallet_balance).toLocaleString("en-IN")}
                  </div>
                  {r.wallet_low && (
                    <div className="text-[9px] uppercase tracking-[0.18em] text-[#B58412] font-bold"
                         data-testid={`mrc-wallet-low-${r.customer_id}`}>Low</div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {r.bill_pending ? (
                    <>
                      <div className="font-mono text-[13px] font-bold text-[#DC2626]"
                           data-testid={`mrc-due-${r.customer_id}`}>
                        ₹{Number(r.due_total).toLocaleString("en-IN")}
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#DC2626] font-bold">
                        {r.due_days != null ? `${r.due_days}d overdue` : "pending"}
                      </div>
                    </>
                  ) : (<span className="text-[10px] text-[#8A8780] italic">None</span>)}
                </td>
                <td className="px-4 py-2.5 text-[11px]">
                  {r.customer_created_at ? (
                    <div className="text-[#1A1A1A] font-mono">
                      {new Date(r.customer_created_at).toLocaleDateString("en-IN", {
                        day: "2-digit", month: "short", year: "2-digit",
                      })}
                    </div>
                  ) : "—"}
                  {r.responsible_field_staff_name && (
                    <div className="text-[10px] text-[#5C5A55]">
                      Field: <strong className="text-[#1A3317]">{r.responsible_field_staff_name}</strong>
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Link to={`/customers/${r.customer_id}`}
                        data-testid={`mrc-open-${r.customer_id}`}
                        className="inline-flex items-center gap-1 text-xs font-bold text-[#2D5A27] hover:underline">
                    Open <ArrowRight size={11} weight="bold"/>
                  </Link>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
